import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Checkbox, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputField } from 'src/components/atoms/input-field';
import { Query } from 'src/components/atoms/query';
import { CardWithContent } from 'src/components/molecules/card-with-content';
import { DataStatusIndicator } from 'src/components/molecules/data-status-indicator/data-status-indicator';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { CompactPagination } from 'src/components/organisms/compact-pagination';
import { useCreateEmployeesGroup } from 'src/features/employees-groups/api/hooks/use-create-employees-group';
import { useFormValidationSchema } from 'src/features/employees-groups/components/organisms/dialogs/create-employees-group-dialog/schema/use-form-validation-schema';
import { fetchEmployees } from 'src/features/employees/api/lib/employees';
import { employeesKeys } from 'src/features/employees/api/query-keys/employees';
import { useMeilisearch } from 'src/hooks/meilisearch/use-meilisearch';
import { usePagination } from 'src/hooks/use-pagination';
import { useSearchState } from 'src/hooks/use-search-state';
import { useUserStore } from 'src/store/user-store';
import { z } from 'zod';

interface Props extends DialogActions {}

export const CreateEmployeesGroupDialog = ({ open, onCancel }: Props) => {
  const { t } = useTranslation('employeesGroups');

  const { directoryToken } = useUserStore();

  const formValidationSchema = useFormValidationSchema();

  type FormValues = z.infer<typeof formValidationSchema>;

  const defaultValues: FormValues = {
    name: '',
    employeesTokens: [],
  };

  const { handleSubmit, reset, control, setValue, watch } = useForm<FormValues>(
    {
      resolver: zodResolver(formValidationSchema),
      mode: 'onBlur',
      defaultValues,
    },
  );

  const {
    page,
    perPage,
    changePage,
    changePerPage,
    setTotalItems,
    totalPages,
    nextDisabled,
    nextPage,
    prevDisabled,
    prevPage,
  } = usePagination();

  const [searchState, setSearchState] = useSearchState({
    q: '',
    filter: ['employeeGroupToken NOT EXISTS OR employeeGroupToken IS NULL'],
  });

  const { results, isLoading, isFetching, error } = useMeilisearch({
    fetch: fetchEmployees,
    page,
    perPage,
    queryKey: employeesKeys.list(
      directoryToken ?? '',
      perPage,
      page,
      searchState,
    ),
    searchState,
    enabled: open,
  });

  useEffect(() => {
    if (!results) return;
    setTotalItems(results.estimatedTotalHits);
  }, [results]);

  const handleOnCancel = () => {
    onCancel?.();
    reset(defaultValues);
  };

  const { mutate: createEmployeesGroup, isPending: isCreatingEmployeesGroup } =
    useCreateEmployeesGroup(handleOnCancel);

  const selectedEmployeesTokens = watch('employeesTokens');

  const handleUpdateSelectedEmployeesTokens = (token: string) => {
    if (selectedEmployeesTokens.includes(token)) {
      setValue(
        'employeesTokens',
        selectedEmployeesTokens.filter(item => item !== token),
      );

      return;
    }

    setValue('employeesTokens', [...selectedEmployeesTokens, token]);
  };

  const handleChangeQuery = (query: string) => {
    setSearchState(prev => ({ ...prev, q: query }));
  };

  const handleCreateEmployeesGroup = ({
    name,
    employeesTokens,
  }: FormValues) => {
    createEmployeesGroup({ name, employeesTokens });
  };

  return (
    <Dialog
      title={t('common.createEmployeesGroupDialog.title')}
      open={open}
      onCancel={handleOnCancel}
      onConfirm={handleSubmit(handleCreateEmployeesGroup)}
      isConfirmButtonSubmitting={isCreatingEmployeesGroup}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Controller
          control={control}
          name="name"
          render={({
            field: { name, value, onChange, onBlur },
            fieldState: { error, invalid },
          }) => (
            <InputField
              name={name}
              label={t('common.createEmployeesGroupDialog.name')}
              fullWidth
              required
              onBlur={onBlur}
              onChange={onChange}
              value={value}
              error={invalid}
              helperText={error?.message}
            />
          )}
        />

        <Typography variant="body2">
          {t('common.createEmployeesGroupDialog.selectEmployees')}
        </Typography>

        <Query
          searchAsType
          disabled={false}
          onChange={(newQuery: string) => {
            handleChangeQuery(newQuery);
          }}
          sx={{
            minWidth: 200,
          }}
          value={searchState.q}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            mt: 2,
          }}>
          {results?.hits?.map(employee => (
            <CardWithContent
              key={employee.token}
              isSelected={selectedEmployeesTokens.includes(employee.token)}
              onClick={() => {
                handleUpdateSelectedEmployeesTokens(employee.token);
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox
                  checked={selectedEmployeesTokens.includes(employee.token)}
                />
                <Typography variant="body2">{employee.email}</Typography>
              </Box>
            </CardWithContent>
          ))}

          <DataStatusIndicator
            dataLength={results?.hits?.length ?? 0}
            isFetching={isLoading}
            error={error?.response?.data?.message}
            resourceUnavailableText={t(
              'common.createEmployeesGroupDialog.noEmployees',
            )}
          />
        </Box>

        <CompactPagination
          disabled={isLoading}
          isFetching={isFetching}
          page={page}
          changePage={changePage}
          handlePerPageChange={changePerPage}
          nextDisabled={nextDisabled}
          nextPage={nextPage}
          prevDisabled={prevDisabled}
          prevPage={prevPage}
          perPage={perPage}
          totalPages={totalPages}
        />
      </Box>
    </Dialog>
  );
};
